import * as R from 'rambdax'
import {isPlainObject} from 'tizra'
import {dropLastWhile, takeLastWhile} from 'tizra/functional'

/**
 * Similar to Object.assign(), but acting on properties in order to
 * preserve getters and setters. Modified from example at
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign
 */
export function completeAssign(target, ...sources) {
  target = isPlainObject(target) ? target : {}
  sources.filter(isPlainObject).forEach(source => {
    const descriptors = Object.keys(source).reduce((descriptors, key) => {
      descriptors[key] = Object.getOwnPropertyDescriptor(source, key)
      return descriptors
    }, {})
    // by default, Object.assign copies enumerable Symbols too
    Object.getOwnPropertySymbols(source).forEach(sym => {
      const descriptor = Object.getOwnPropertyDescriptor(source, sym)
      if (descriptor.enumerable) {
        descriptors[sym] = descriptor
      }
    })
    Object.defineProperties(target, descriptors)
  })
  return target
}

const createDeepAssigner = assigner => {
  function assignDepth(target, sources, maxDepth, depth = 0) {
    target = assigner(target, ...sources)
    if (depth < maxDepth) {
      Object.keys(target)
        .filter(k => Object.getOwnPropertyDescriptor(target, k).writable)
        .filter(k => isPlainObject(target[k]))
        .forEach(k => {
          const isSuitableKeySource = source => {
            const sd = Object.getOwnPropertyDescriptor(source, k)
            return sd && sd.writable && isPlainObject(source[k])
          }
          const keySources = R.pipe(
            R.filter(isPlainObject),
            dropLastWhile(R.complement(isSuitableKeySource)),
            takeLastWhile(isSuitableKeySource),
            R.map(R.prop(k)),
          )(sources)
          if (keySources.length) {
            keySources.unshift(target[k])
            target[k] = assignDepth({}, keySources, maxDepth, depth + 1)
          }
        })
    }
    return target
  }
  return assignDepth
}

export const completeAssignDepth = createDeepAssigner(completeAssign)

export const objectAssignDepth = createDeepAssigner(Object.assign)

export const deepCompleteAssign = (target, ...sources) =>
  completeAssignDepth(target, sources, 64)

export const deepObjectAssign = (target, ...sources) =>
  objectAssignDepth(target, sources, 64)
